import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { chatService } from '../../services/chatService';

interface RfiQuestionsProps {
  onCitationClick: (citationId: string, page: number, citation?: { file_name?: string; fund?: string; as_of_date?: string }) => void;
}

export interface RfiQuestionsRef {
  handleFileUploaded: (questions: any[], threadId: string) => void;
}

const RfiQuestions = forwardRef<RfiQuestionsRef, RfiQuestionsProps>(({
  onCitationClick
}, ref) => {
  const [extractedQuestions, setExtractedQuestions] = useState<any[]>([]);
  const [rfiThreadId, setRfiThreadId] = useState<string>('');
  const [rfiConversations, setRfiConversations] = useState<any[]>([]);
  const [expandedAnswers, setExpandedAnswers] = useState<Set<number>>(new Set());
  const [rfiPollingInterval, setRfiPollingInterval] = useState<NodeJS.Timeout | null>(null);

  const startRfiPolling = (threadId: string) => {
    console.log('Starting RFI polling for thread:', threadId);
    if (rfiPollingInterval) {
      clearInterval(rfiPollingInterval);
    }
    
    const interval = setInterval(async () => {
      try {
        const response = await chatService.getRfiChatThread(threadId);
        console.log('RFI polling response:', response);
        if (response.data.conversations) {
          setRfiConversations(response.data.conversations);
          console.log('Conversations is set:', rfiConversations);
        }
      } catch (error) {
        console.error('RFI polling error:', error);
      }
    }, 5000);
    
    setRfiPollingInterval(interval);
  };

  const stopRfiPolling = () => {
    if (rfiPollingInterval) {
      clearInterval(rfiPollingInterval);
      setRfiPollingInterval(null);
    }
  };

  const handleFileUploaded = (questions: any[], threadId: string) => {
    setExtractedQuestions(questions);
    if (threadId) {
      setRfiThreadId(threadId);
      startRfiPolling(threadId);
    }
  };

  const handleDownloadWordDocument = async () => {
    if (!rfiThreadId) return;
    
    try {
      const response = await fetch(`/api/rfi/thread/${rfiThreadId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rfi-document-${rfiThreadId}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  const toggleAnswerExpansion = (index: number) => {
    setExpandedAnswers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const truncateText = (text: string, maxLines: number = 3) => {
    const lines = text.split('\n');
    if (lines.length <= maxLines) return text;
    
    const truncated = lines.slice(0, maxLines).join('\n');
    return truncated + '...';
  };

  useImperativeHandle(ref, () => ({
    handleFileUploaded
  }));

  useEffect(() => {
    return () => {
      stopRfiPolling();
    };
  }, []);

  return (
    <div className="card-elevated">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Questions</h1>
      {extractedQuestions.length > 0 ? (
        <div className="space-y-4">
          {extractedQuestions.map((question, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-800 mb-2">
                {index + 1}. {question.question}
              </h3>
              {question.sub_questions && question.sub_questions.length > 0 && (
                <div className="ml-4 space-y-2">
                  {question.sub_questions.map((subQ: any, subIndex: number) => (
                    <div key={subIndex} className="text-sm text-gray-600">
                      • {subQ}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {/* Processing indicator */}
          {rfiThreadId && rfiConversations.length === 0 && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center text-blue-800">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                <span>Processing questions...</span>
              </div>
            </div>
          )}
          
          {/* Show conversations if available */}
          {rfiConversations.length > 0 && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="space-y-3">
                {rfiConversations.map((conv, index) => (
                  <div key={index} className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-800 mb-1 bg-gray-100 p-2 rounded">Q: {conv.question}</div>
                    <div className="text-gray-600">
                      A: {expandedAnswers.has(index) 
                        ? conv.answer 
                        : truncateText(conv.answer || 'Processing...')
                      }
                      {conv.answer && conv.answer.split('\n').length > 3 && (
                        <button 
                          onClick={() => toggleAnswerExpansion(index)}
                          className="ml-2 text-blue-600 hover:text-blue-800 text-sm italic"
                        >
                          {expandedAnswers.has(index) ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </div>
                    {conv.citations && conv.citations.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <div className="text-sm text-gray-600">
                          Citations: {conv.citations.map((citation: any, idx: number) => (
                            <span 
                              key={idx} 
                              className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded mr-1 mb-1 cursor-pointer hover:bg-blue-200 transition-colors"
                              onClick={() => onCitationClick(citation.id, citation.page || 1, {
                                file_name: citation.file_name,
                                fund: citation.fund,
                                as_of_date: citation.as_of_date
                              })}
                            >
                              {citation.chunk_index}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Download button */}
          {rfiThreadId && (
            <div className="mt-6">
              <button 
                onClick={handleDownloadWordDocument}
                className="btn-primary w-full"
              >
                Download Word Document
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-gray-500 text-center py-8">
          Upload an RFI file to see extracted questions
        </div>
      )}
    </div>
  );
});

RfiQuestions.displayName = 'RfiQuestions';

export default RfiQuestions;
